import { Button } from "@/components/Button/Button";
import { OnboardingCard } from "@/pages/Onboarding/Components/OnboardingCard";
import { OnboardingHeader } from "@/pages/Onboarding/Components/OnboardingHeader";
import { useCreateOrganisationApiV1OrganisationsPostMutation, useUpdateUserApiV1UsersUserIdPatchMutation } from "@/store/clientApi";
import { OnboardingUserData } from "@/typed/OnboardingUserData";
import { Countries, ensureError, localizeError } from "@/utils/helpers";
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Theme, Typography } from "@mui/material";
import { jwtDecode, JwtPayload } from "jwt-decode";
import React, { FC, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

/** UNSUPPORTED_REGION_ERROR is temporarily used to prevent users
 * selecting countries other than Australia */
const UNSUPPORTED_REGION_ERROR = "Unsupported Region";

export const OnboardingForm: FC = () => {
  const { t } = useTranslation("onboarding");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [fullName, setFullName] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [country, setCountry] = useState("AU");
  const [countryError, setCountryError] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [orgError, setOrgError] = useState("");
  const [createOrg] = useCreateOrganisationApiV1OrganisationsPostMutation();
  const [updateUser] = useUpdateUserApiV1UsersUserIdPatchMutation();

  const token = new URL(window.location.href).searchParams.get("session_token");
  const state = new URL(window.location.href).searchParams.get("state");
  const ref = new URL(window.location.href).searchParams.get("ref");

  const submitForm = async () => {
    setLoadingSubmit(true);
    if (fullName === "") {
      setFullNameError("Field is required");
    } else {
      setFullNameError("");
    }

    if (country === "") {
      setCountryError("Field is required");
    } else if (country !== "AU") {
      setCountryError(UNSUPPORTED_REGION_ERROR);
    } else {
      setCountryError("");
    }

    if (organisation === "") {
      setOrgError("Field is required");
    } else {
      setOrgError("");
    }

    if (fullName !== "" && country !== "" && organisation !== "") {
      const newData: OnboardingUserData = {
        fullName: fullName,
        country: country,
        organisation: organisation
      };

      const decoded = jwtDecode<JwtPayload & { sub: string; continue_uri: string }>(token ?? "");
      const userId = decoded.sub;

      try {
        await createOrg({
          organisationCreateRequestDto: {
            name: newData.organisation,
            affiliate_code: ref ?? undefined
          }
        }).unwrap();

        await updateUser({
          userId: userId,
          updateUserRequest: {
            name: newData.fullName
          }
        }).unwrap();

        window.location.href = `${decoded.continue_uri}?state=${state}`;
      } catch (err) {
        const error = ensureError(err);
        toast.error(localizeError(t, error));
        setLoadingSubmit(false);
      }
    }
    setLoadingSubmit(false);
  };

  const handleOnChangeFullName = (nameValue: string) => {
    if (nameValue === "") {
      setFullNameError("Field is required");
    } else {
      setFullNameError("");
    }

    setFullName(nameValue);
  };

  const handleOnChangeCountry = (countryValue: string) => {
    if (countryValue === "") {
      setCountryError("Field is required");
    } else if (countryValue !== "AU") {
      setCountryError(UNSUPPORTED_REGION_ERROR);
    } else {
      setCountryError("");
    }

    setCountry(countryValue);
  };

  const handleOnOrgChange = (orgValue: string) => {
    if (orgValue === "") {
      setOrgError("Field is required");
    } else {
      setOrgError("");
    }

    setOrganisation(orgValue);
  };

  return (
    <OnboardingCard>
      <OnboardingHeader />
      <Box
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "0.8em"
        }}
        noValidate
        autoComplete="off"
      >
        <FormControl fullWidth>
          <TextField
            id="full-name"
            label={t("label.fullName")}
            variant="outlined"
            error={!!fullNameError}
            value={fullName}
            onChange={(e) => handleOnChangeFullName(e.target.value)}
            required
          />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="country-label" required error={!!countryError}>
            {t("label.country")}
          </InputLabel>
          <Select
            labelId="country-label"
            id="country-select"
            label={t("label.country")}
            value={country}
            error={!!countryError}
            onChange={(e) => handleOnChangeCountry(e.target.value)}
            required
          >
            {Countries.raw.map((item) => (
              <MenuItem key={item.name} value={item.code}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
          {countryError === UNSUPPORTED_REGION_ERROR && (
            <Typography
              sx={{
                color: (theme: Theme) => theme.palette.error.main,
                fontSize: "0.8rem",
                fontWeight: "500",
                marginTop: "4px"
              }}
            >
              {t("message.regionError")}
            </Typography>
          )}
        </FormControl>
        <TextField
          id="organisation-name"
          label={t("label.organisationName")}
          variant="outlined"
          error={!!orgError}
          value={organisation}
          onChange={(e) => handleOnOrgChange(e.target.value)}
          required
        />
        <Button
          disabled={country !== "AU" || loadingSubmit}
          sx={{
            marginTop: "24px",
            height: "52px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          type="submit"
          onClick={(e: React.SyntheticEvent) => {
            e.preventDefault();
            submitForm();
          }}
        >
          {t("common:button.continue")}
        </Button>
      </Box>
    </OnboardingCard>
  );
};
